import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Form, Button, Pagination, Table, Spinner, Accordion } from "react-bootstrap";

import ReactDOMServer from "react-dom/server";
import EditIcon from "../../../components/icon/EditIcon";
import useSalesList from "../../../hooks/sales/useSalesList";
import { formatDate, formatDate2 } from "../../../utilities/dateFormatter";
import useUserMe from "../../../hooks/useUserMe";
import { formatNumber } from "../../../utilities/formatNumber";
import { deleteSales, getSalesListExport } from "../../../api/sales";
import DeleteIcon from "../../../components/icon/DeleteIcon";
import exportToExcel from "../../../utilities/exportToExcel";

//flatpicker
import Flatpickr from "react-flatpickr";
import { Korean } from "flatpickr/dist/l10n/ko.js"; // Import 한국어 locale
import { getTodayDate } from "../../../utilities/dateUtils";
import { dateSearch } from "../../../utilities/dateUtils";

import { toast } from "react-toastify";
import { MdAssignment, MdDelete } from "react-icons/md";
import { BiDownload } from "react-icons/bi";

// 결제수단: 카드/계좌/현금/QR/바우처 금액을 한 줄로 표시 (camelCase / snake_case 모두 처리)
const formatPaymentMethodBreakdown = (payment, formatNum) => {
  if (!payment) return "-";
  const card = formatNum(payment.cardPrice ?? payment.card_price ?? 0);
  const bank = formatNum(payment.bankPrice ?? payment.bank_price ?? 0);
  const cash = formatNum(payment.cashPrice ?? payment.cash_price ?? 0);
  const qr = formatNum(payment.qrPrice ?? payment.qr_price ?? 0);
  const voucher = formatNum(payment.voucherPrice ?? payment.voucher_price ?? 0);
  return `카드 ${card} / 계좌 ${bank} / 현금 ${cash} / QR ${qr} / 바우처 ${voucher}`;
};

const SalesList = () => {
  const navigate = useNavigate();
  const { data: userMeData } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;

  const todayDate = getTodayDate();
  const [searchParams, setSearchParams] = useSearchParams();
  const flatpickrRef = useRef(null);

  // --- 상태 초기화: 쿼리스트링 기반으로 ---
  const getInit = (key, fallback) => {
    const v = searchParams.get(key);
    return v ? v : fallback;
  };
  const [page, setPage] = useState(() => parseInt(getInit("page", "1")));
  const [size, setSize] = useState(() => parseInt(getInit("size", "10")));
  const [dateRange, setDateRange] = useState(() => {
    const start = searchParams.get("startDate");
    const end = searchParams.get("endDate");
    if (start && end) return [start, end];
    return [todayDate, todayDate];
  });
  const [searchStartDate, setSearchStartDate] = useState(dateRange[0]);
  const [searchEndDate, setSearchEndDate] = useState(dateRange[1]);
  const [isExporting, setIsExporting] = useState(false);

  const { salesList, totalCount, loading, refresh } = useSalesList(
    storeId, searchStartDate, searchEndDate, false, page, size
  );

  // 쿼리스트링에서 dateRange 값도 읽어오기 (YYYY-MM-DD 형식으로)
  useEffect(() => {
    const pageParam = searchParams.get("page");
    const sizeParam = searchParams.get("size");
    const startParam = searchParams.get("startDate");
    const endParam = searchParams.get("endDate");
    if (pageParam) setPage(parseInt(pageParam));
    if (sizeParam) setSize(parseInt(sizeParam));
    if (startParam && endParam) {
      setDateRange([startParam, endParam]);
      setSearchStartDate(startParam);
      setSearchEndDate(endParam);
    }
  }, []);

  // --- 쿼리스트링 → 상태 (항상 동기화) ---
  useEffect(() => {
    const pageParam = parseInt(searchParams.get("page") || "1");
    const sizeParam = parseInt(searchParams.get("size") || "10");
    const startParam = searchParams.get("startDate");
    const endParam = searchParams.get("endDate");

    if (page !== pageParam) setPage(pageParam);
    if (size !== sizeParam) setSize(sizeParam);
    if (startParam && endParam) {
      if (dateRange[0] !== startParam || dateRange[1] !== endParam) {
        setDateRange([startParam, endParam]);
        setSearchStartDate(startParam);
        setSearchEndDate(endParam);
      }
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // --- 상태 → 쿼리스트링은 직접 액션에서만! ---
  const buildParams = (pageValue, sizeValue) => {
    const params = { page: String(pageValue), size: String(sizeValue) };
    if (dateRange[0]) params.startDate = dateRange[0];
    if (dateRange[1]) params.endDate = dateRange[1];
    return params;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, size));
  };
  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(1);
    setSearchParams(buildParams(1, newSize));
  };
  const handleSearch = () => {
    setSearchStartDate(dateRange[0]);
    setSearchEndDate(dateRange[1]);
    setPage(1);
    setSearchParams(buildParams(1, size));
  };

  const handleDelete = useCallback(async (id) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      try {
        await deleteSales(id);
        alert("삭제되었습니다.");
        refresh(); // 데이터 갱신
      } catch (error) {
        alert(error.message);
      }
    }
  }, [refresh]);

  const handleReset = () => {
    setDateRange([todayDate, todayDate]);
    setSearchStartDate(todayDate);
    setSearchEndDate(todayDate);
    setPage(1);
    setSearchParams({ page: "1", size: "10" });
    flatpickrRef.current?.flatpickr?.clear();
  };

  // 엑셀 내보내기 버튼 핸들러
  const handleExportToExcel = async () => {
    if (isExporting) {
      toast.warn("엑셀 파일이 준비 중입니다. 잠시만 기다려주세요.");
      return;
    }
    setIsExporting(true);
    let loadingToastId = null;

    try {
      // react-toastify로 토스트 표시
      loadingToastId = toast.info("엑셀 파일을 준비 중입니다...", { autoClose: false });
      // 날짜를 YYYY-MM-DD로 변환
      const formatDateParam = (d) => {
        if (!d) return null;
        if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
        const dateObj = new Date(d);
        if (isNaN(dateObj)) return null;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const startDateParam = formatDateParam(searchStartDate);
      const endDateParam = formatDateParam(searchEndDate);

      // 커스텀 API 함수 사용
      const data = await getSalesListExport(storeId, startDateParam, endDateParam);
      // 엑셀 헤더 정의
      const headers = [
        "번호", "이름", "구분", "프로그램", "결제방법", "결제수단", "결제금액", "프로그램금액",
        "잔금", "작성자", "생성일", "메모"
      ];
      const formattedData = data.map((row, index) => ({
        번호: index + 1,
        이름: row.user ? row.user.name : "-",
        구분: row.saleType === '직접입력' ? row.saleTypeInput : row.saleType,
        프로그램: row.program?.name || "-",
        결제방법: row.payMethod || "-",
        결제수단: formatPaymentMethodBreakdown(row.payment, formatNumber),
        결제금액: formatNumber(row.payment.totalPrice),
        프로그램금액: formatNumber(row.payment.price),
        잔금: formatNumber(row.payment.receivePrice),
        작성자: row.instructorUser?.name || "-",
        생성일: formatDate2(row.createdAt),
        메모: row.memo || "-"
      }));
      exportToExcel(formattedData, "sales_data", headers);
      toast.dismiss(loadingToastId);
      toast.success("엑셀 파일이 다운로드되었습니다.");
    } catch (err) {
      if (loadingToastId) toast.dismiss(loadingToastId);
      toast.error("엑셀 다운로드에 실패했습니다.");
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / size);

  const getPageNumbers = () => {
    const maxVisible = 10;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div>
      {/* ✅ 검색 옵션 (권한: 관리자 전용) */}
      {userMeData && userMeData.instructor.position === 'DIRECTOR' && (
        <Accordion defaultActiveKey="0" className="mb-3">
          <Accordion.Item
            eventKey="0"
            style={{ maxWidth: "600px", fontSize: "0.875rem" }}
          >
            <Accordion.Header>검색 옵션</Accordion.Header>
            <Accordion.Body>
              <Row className="align-items-center mb-1 mt-1 gx-1 gy-1">
                <Col md={2}><Form.Label>등록일</Form.Label></Col>
                <Col md={6}>
                  <Flatpickr
                    ref={flatpickrRef}
                    className="form-control"
                    options={{ locale: Korean, mode: "range", dateFormat: "Y-m-d" }}
                    value={dateRange}
                    onChange={(dates) => {
                      dateSearch(dates, (start, end) => {
                        setDateRange([start, end]);
                      });
                    }}
                    placeholder="날짜를 선택하세요"
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col className="text-end">
                  <Button onClick={handleSearch}>검색</Button>
                  <Button variant="secondary" className="ms-2" onClick={handleReset}>초기화</Button>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
      


      {/* ✅ 검색 외부에 위치한 상단 우측 정보 */}
      <Row className="mb-2 justify-content-end">
        {/* 엑셀 다운로드 버튼 (권한: 관리자 전용) */}
        {userMeData && userMeData.instructor.position === 'DIRECTOR' && (
        <Col md="auto" className="text-start">
          <Button
            variant="success"
            className="me-2"
            onClick={handleExportToExcel}
            disabled={isExporting}
          >
            <BiDownload size={18} className="me-1" />
            엑셀
          </Button>
        </Col>
        )}
        <Col md="auto" className="text-end">
          <span className="me-3">
            총 {totalCount}건 / {totalPages}페이지
          </span>
          <Form.Select
            value={size}
            onChange={(e) => handleSizeChange(Number(e.target.value))}
            style={{ width: "100px", display: "inline-block" }}
          >
            {[10, 20, 50, 100].map((s) => (
              <option key={s} value={s}>{s}개씩</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* ✅ 사용자 목록 테이블 */}
      {loading ? (
        <div className="text-center my-4"><Spinner animation="border" /></div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>번호</th>
                <th>이름</th>
                <th>구분</th>
                <th>결제방법</th>
                <th>결제수단</th>
                <th>프로그램</th>
                <th>결제금액</th>
                <th>프로그램금액</th>
                <th>잔금</th>
                <th>작성자</th>
                <th>생성일</th>
                <th>메모</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {salesList.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center">검색결과가 없습니다.</td>
                </tr>
              ) : (
                salesList.map((data, idx) => (
                  <tr key={data.id}>
                    <td>{(page - 1) * size + idx + 1}</td>
                    <td>
                      <a href={`/user/user-edit/${data.user?.id}`}>{data.user?.name}</a>
                    </td>
                    <td>{data.saleType === '직접입력' ? data.saleTypeInput : data.saleType}</td>
                    <td>{data.payMethod}</td>
                    <td className="text-nowrap" style={{ fontSize: "0.8rem" }}>
                      {formatPaymentMethodBreakdown(data.payment, formatNumber)}
                    </td>
                    <td>
                      {data.program ? (data.program.name) : ("-")}
                    </td>
                    <td>
                      {data.saleType === '환불' ? 
                        <span className="text-danger">{formatNumber(data.payment.totalPrice)}</span> : 
                        formatNumber(data.payment.totalPrice)}
                    </td>
                    <td>{formatNumber(data.payment.price)}</td>
                    <td>
                      {data.payment.receivePrice > 0 ? 
                        <span className="text-danger">{formatNumber(data.payment.receivePrice)}</span> : 
                        formatNumber(data.payment.receivePrice)}
                    </td>
                    <td>{data.instructorUser?.name ?? "-"}</td>
                    <td>{formatDate2(data.createdAt)}</td>
                    <td>{data.memo ?? "-"}</td>
                    <td>
                      {/* 수정 */}
                      <Button
                        size="sm"
                        variant="outline-warning"
                        href={`/sales/sales-edit/${data.id}`}
                      >
                        <EditIcon size={18} />
                      </Button>
                      {" "}
                      {/* 삭제 */}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(data.id)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* ✅ 페이지네이션 */}
          <Pagination className="justify-content-center mt-2">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1} />
            {getPageNumbers().map((p) => (
              <Pagination.Item
                key={p}
                active={p === page}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => handlePageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
          </Pagination>
        </>
      )}
    </div>
  );
};

export default SalesList;
